import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";

const TOOLBAR_BUTTON_SIZE = "30px";
const HEADER_BUTTON_SIZE = "32px";
const TOOLBAR_ROW_BOTTOM_PADDING = "2px";

export const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflowY: "hidden",
    overflowX: "hidden",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: designTokens.spacing[4],
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.lg,
  },
  userMessage: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    width: "100%",
  },
  messageContent: {
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[4],
    wordBreak: "break-word",
    fontSize: designTokens.fontSize.sm,
  },
  inputCard: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: designTokens.neutral.white,
    border: `2px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius["2xl"],
    boxShadow: designTokens.shadow.md,
    overflow: "hidden",
    ":focus-within": {
      border: `2px solid ${designTokens.accent[500]}`,
    },
  },
  textArea: {
    width: "100%",
    ...shorthands.border("none"),
    outline: "none",
    backgroundColor: designTokens.neutral.white,
    resize: "none",
    fontSize: designTokens.fontSize.sm,
    minHeight: "28px",
    boxSizing: "border-box",
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    ":hover": {
      ...shorthands.border("none"),
    },
    ":focus": {
      ...shorthands.border("none"),
    },
    ":focus-within": {
      ...shorthands.border("none"),
    },
    ":active": {
      ...shorthands.border("none"),
    },
    "::after": {
      display: "none",
    },
  },
  innerTextarea: {
    lineHeight: designTokens.spacing[5],
  },
  innerTextareaBusy: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    justifyContent: "space-between",
    paddingTop: "0",
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[1],
    paddingBottom: TOOLBAR_ROW_BOTTOM_PADDING,
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  sendButton: {
    width: TOOLBAR_BUTTON_SIZE,
    height: TOOLBAR_BUTTON_SIZE,
    minWidth: TOOLBAR_BUTTON_SIZE,
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: projectIqTokens.teal,
    ...shorthands.border("none"),
    color: designTokens.neutral.white,
    ...shorthands.padding("0"),
    ...shorthands.margin(designTokens.spacing[1]),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      backgroundColor: `color-mix(in srgb, ${projectIqTokens.teal} 80%, black)`,
      transform: "scale(1.05)",
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  sendButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  finishSessionButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    border: `1px solid ${designTokens.neutral[300]}`,
    backgroundColor: designTokens.neutral.white,
    color: designTokens.neutral[600],
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    ...shorthands.padding(designTokens.spacing[2], designTokens.spacing[3]),
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      backgroundColor: designTokens.neutral[50],
      border: `1px solid ${designTokens.neutral[400]}`,
      color: designTokens.neutral[700],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  finishSessionButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  finishSessionArea: {
    borderTop: `1px solid ${designTokens.neutral[200]}`,
    padding: designTokens.spacing[4],
    display: "flex",
    justifyContent: "flex-end",
  },
  skeletonContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  skeletonItemHeader: {
    width: "120px",
  },
  skeletonItemWide: {
    width: "var(--skeleton-width)" as string,
  },
  skeletonItemNarrow: {
    width: "var(--skeleton-width-narrow)" as string,
  },
  skeletonItem80: { width: "80%" },
  skeletonItem60: { width: "60%" },
  skeletonItem20: { width: "20%" },
  configErrorBanner: {
    backgroundColor: projectIqTokens.rustBg,
    border: `1px solid ${projectIqTokens.rust}`,
    borderRadius: designTokens.borderRadius.md,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    color: projectIqTokens.rust,
    fontSize: designTokens.fontSize.sm,
    marginBottom: designTokens.spacing[3],
  },
  loadErrorBanner: {
    backgroundColor: projectIqTokens.rustBg,
    border: `1px solid ${projectIqTokens.rust}`,
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing[4],
    color: projectIqTokens.rust,
    fontSize: designTokens.fontSize.sm,
    margin: designTokens.spacing[4],
  },
  confirmBanner: {
    backgroundColor: "#ECFDF5",
    borderLeft: `4px solid #16A34A`,
    padding: designTokens.spacing[4],
    borderRadius: designTokens.borderRadius.md,
    marginBottom: designTokens.spacing[4],
    color: "#166534",
    fontSize: designTokens.fontSize.sm,
  },
  messageBarArea: {
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    boxSizing: "border-box",
  },
  contentSourceButtonWrapper: {
    position: "relative",
    display: "inline-flex",
  },
  contentSourceButton: {
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: TOOLBAR_BUTTON_SIZE,
    height: TOOLBAR_BUTTON_SIZE,
    ...shorthands.padding("0"),
    boxSizing: "border-box",
    backgroundColor: designTokens.neutral.white,
    color: designTokens.neutral[600],
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      color: designTokens.accent[500],
      border: `1px solid ${designTokens.neutral[300]}`,
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  contentSourceBadge: {
    position: "absolute",
    top: "-4px",
    left: "-6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "10px",
    height: "18px",
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    paddingLeft: designTokens.spacing[1],
    paddingRight: designTokens.spacing[1],
    pointerEvents: "none",
  },
  welcomeArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[3],
    padding: `40px ${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
  },
  welcomeBadge: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.xs,
  },
  welcomeBadgeIcon: {
    color: "#16A34A",
    backgroundColor: "#ECFDF5",
    width: "16px",
    height: "16px",
    borderRadius: designTokens.borderRadius.sm,
    padding: designTokens.spacing[1],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  welcomeTitle: {
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: "700",
    color: designTokens.neutral[900],
    textAlign: "center",
  },
  welcomeSubtitle: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[500],
    textAlign: "center",
    maxWidth: "480px",
    lineHeight: "1.5",
  },
  uploadButton: {
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: TOOLBAR_BUTTON_SIZE,
    height: TOOLBAR_BUTTON_SIZE,
    ...shorthands.padding("0"),
    boxSizing: "border-box",
    backgroundColor: designTokens.neutral.white,
    color: designTokens.neutral[600],
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      color: designTokens.accent[500],
      border: `1px solid ${designTokens.neutral[300]}`,
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  chatHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[4],
    paddingRight: designTokens.spacing[2],
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    flexShrink: 0,
  },
  chatHeaderTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
  },
  expandButton: {
    minWidth: HEADER_BUTTON_SIZE,
    width: HEADER_BUTTON_SIZE,
    height: HEADER_BUTTON_SIZE,
    padding: "0",
    marginLeft: "auto",
  },
  expandedRoot: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    zIndex: 9999,
    backgroundColor: designTokens.neutral.white,
    borderRadius: "0",
  },
  messageActions: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: designTokens.spacing[1],
    paddingTop: designTokens.spacing[1],
  },
  messageActionButton: {
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    ...shorthands.padding(designTokens.spacing[1]),
    borderRadius: designTokens.borderRadius.sm,
    color: designTokens.neutral[400],
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      color: designTokens.neutral[700],
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  messageActionError: {
    ...shorthands.flex(1, 1, "100%"),
    color: projectIqTokens.rust,
    fontSize: designTokens.fontSize.sm,
  },
});
